import { useState, useEffect, useMemo, useRef } from "react";
import { api } from "../api";
import {
  ChevronRight, ChevronDown, Loader2, BarChart3, Filter,
  Calendar, Stethoscope, Building2, UserCircle, LayoutList,
  X, Search, Info
} from "lucide-react";

interface TreeNode {
  name: string;
  level: string;
  unit_name?: string;
  specialty_name?: string;
  values: Record<string, number | null>;
  deltas: Record<string, number | null>;
  children: TreeNode[];
}

interface VersionInfo {
  version: string;
  upload_count: number;
  total_records: number;
  last_report_date: string;
  last_upload: string;
}

const ENTITY_LEVELS = [
  { value: "", label: "All levels", icon: LayoutList },
  { value: "group", label: "Total GTS", icon: Building2 },
  { value: "specialty", label: "By Specialty", icon: Stethoscope },
  { value: "unit", label: "By Unit", icon: Building2 },
  { value: "doctor", label: "By Doctor", icon: UserCircle },
];

const PERIOD_TYPES = [
  { value: "month", label: "Monthly" },
  { value: "day", label: "Daily" },
  { value: "total", label: "Total" },
];

// Sheets that only contain data for a specific period type
const DEFAULT_PERIOD_TYPE: Record<string, string> = {
  "Consulta": "month",
  "Faturação por Especialidade": "month",
  "Faturação por Serviço": "total",
  "Cirurgia": "month",
  "Tratamentos": "total",
  "Med100%": "total",
  "Cardiologia Pediátrica": "total",
  "Disponibilidades": "month",
  "Bloqueios_Totais": "day",
  "Bloqueios_Parciais": "day",
  "Sem Vagas": "day",
  "Vagas superiores a 60 dias": "day",
  "Lista_Espera": "total",
  "Agendas_UnidadeFuncional": "month",
  "Agendas_dt_fim": "month",
};

const PERIOD_TYPE_OPTIONS: Record<string, string[]> = {
  "Consulta": ["month", "total"],
  "Faturação por Especialidade": ["month", "total"],
  "Faturação por Serviço": ["month", "total"],
  "Cirurgia": ["month", "total"],
  "Tratamentos": ["total"],
  "Med100%": ["total"],
  "Cardiologia Pediátrica": ["total"],
  "Disponibilidades": ["month"],
  "Bloqueios_Totais": ["day"],
  "Bloqueios_Parciais": ["day"],
  "Sem Vagas": ["day"],
  "Vagas superiores a 60 dias": ["day"],
  "Lista_Espera": ["total"],
  "Agendas_UnidadeFuncional": ["month"],
  "Agendas_dt_fim": ["month"],
};

const MONTHS_PT = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

const formatPeriod = (period: string, year?: number | null, reportMonthIndex?: number | null): string => {
  const shortYear = year ? String(year).slice(-2) : null;
  const monthIdx = MONTHS_PT.indexOf(period);
  const isMonth = monthIdx >= 0;
  const isPastOrCurrentMonth = reportMonthIndex == null || monthIdx <= reportMonthIndex;
  if (isMonth && shortYear && isPastOrCurrentMonth) return `${period} ${shortYear}`;
  if (isMonth) return period;
  return period;
};

function useVersion() {
  const [version, setVersion] = useState<VersionInfo | null>(null);
  useEffect(() => {
    fetch("/api/version")
      .then((r) => r.json())
      .then(setVersion)
      .catch(() => {});
  }, []);
  return version;
}

export default function Analysis() {
  const version = useVersion();
  const [sheets, setSheets] = useState<string[]>([]);
  const [sheetMetrics, setSheetMetrics] = useState<Record<string, string[]>>({});
  const [availableSpecialties, setAvailableSpecialties] = useState<string[]>([]);
  const [sheet, setSheet] = useState("Consulta");
  const [metric, setMetric] = useState("Nr Cons N // Δ Orç");
  const [periodType, setPeriodType] = useState("month");
  const [specialty, setSpecialty] = useState("");
  const [unit, setUnit] = useState("");
  const [entityLevel, setEntityLevel] = useState("specialty");
  const [selectedPeriods, setSelectedPeriods] = useState<string[]>([]);
  const [tree, setTree] = useState<TreeNode | null>(null);
  const [periods, setPeriods] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [showFilters, setShowFilters] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Sheets managed by dedicated views instead of the generic analysis tree
  const EXCLUDED_ANALYSIS_SHEETS = ["Cobertura de Agendas", "Horas em Falta + Prioridades"];

  useEffect(() => {
    api.getFilters().then((f) => {
      setSheets((f.sheets || []).filter((s) => !EXCLUDED_ANALYSIS_SHEETS.includes(s)));
    });
    api.getSheetMetrics().then((data) => setSheetMetrics(data || {})).catch(() => {});
  }, []);

  // Update the specialty dropdown so it only lists specialties that actually have data for the selected sheet/metric/period.
  useEffect(() => {
    if (!sheet || !metric || !periodType) return;
    api.getAvailableSpecialties(sheet, metric, periodType)
      .then((d) => {
        const specs = d.specialties || [];
        setAvailableSpecialties(specs);
        if (specialty && !specs.includes(specialty)) {
          setSpecialty("");
        }
      })
      .catch(() => setAvailableSpecialties([]));
  }, [sheet, metric, periodType]);

  // Reset metric/period to the sheet's defaults when sheet changes.
  useEffect(() => {
    const metrics = sheetMetrics[sheet];
    if (metrics && metrics.length > 0) {
      setMetric(metrics[0]);
    }
    const defaultPeriod = DEFAULT_PERIOD_TYPE[sheet] || "month";
    setPeriodType(defaultPeriod);
    setSelectedPeriods([]);
  }, [sheet, sheetMetrics]);

const reportYear = useMemo(() => {
    if (!version?.last_report_date) return null;
    const parts = version.last_report_date.split("/");
    if (parts.length !== 2) return null;
    const y = parseInt(parts[0], 10);
    return isNaN(y) ? null : y;
  }, [version]);
  const reportMonthIndex = useMemo(() => {
    if (!version?.last_report_date) return null;
    const parts = version.last_report_date.split("/");
    if (parts.length !== 2) return null;
    return parseInt(parts[1], 10) - 1;
  }, [version]);

  const reportMonthLabelRaw = useMemo(() => {
    if (reportMonthIndex == null) return null;
    return MONTHS_PT[reportMonthIndex] || null;
  }, [reportMonthIndex]);

  const reportMonthLabel = useMemo(() => {
    return reportMonthLabelRaw && reportYear ? `${reportMonthLabelRaw} ${String(reportYear).slice(-2)}` : reportMonthLabelRaw;
  }, [reportMonthLabelRaw, reportYear]);

  const abortRef = useRef<AbortController | null>(null);

  const loadData = () => {
    setLoading(true);
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const params = new URLSearchParams();
    params.set("sheet", sheet);
    params.set("metric", metric);
    params.set("period_type", periodType);
    if (specialty) params.set("specialty_filter", specialty);
    if (unit) params.set("unit", unit);

    const doFetch = (authRetry = false) =>
      fetch(`/api/data/tree?${params.toString()}`, {
        signal: controller.signal,
        headers: authRetry ? { Authorization: `Bearer ${localStorage.getItem("token") || ""}` } : undefined,
      });

    doFetch()
      .then((r) => {
        const token = localStorage.getItem("token");
        if (!r.ok && token) return doFetch(true);
        return r;
      })
      .then((r) => r.json())
      .then((data) => {
        setTree(data.tree);
        setPeriods(data.periods || []);

        // Auto-expand first two levels
        const exp = new Set<string>();
        if (data.tree) {
          exp.add(data.tree.name);
          data.tree.children?.forEach((c: TreeNode) => {
            if (entityLevel === "" || c.level === entityLevel || (entityLevel === "specialty" && c.level === "specialty")) {
              exp.add(c.name);
            }
          });
        }
        setExpanded(exp);

        // Default period selection: report month for monthly view, all for others
        if (data.periods && data.periods.length > 0) {
          if (periodType === "month" && reportMonthLabelRaw && data.periods.includes(reportMonthLabelRaw)) {
            setSelectedPeriods([reportMonthLabelRaw]);
          } else if (periodType === "day" && reportMonthLabel) {
            // Select nothing initially for daily — too many days
            setSelectedPeriods([]);
          } else {
            setSelectedPeriods(data.periods);
          }
        }
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        setTree(null);
        setPeriods([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
  };

  useEffect(() => {
    if (sheet && metric) loadData();
    return () => {
      abortRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheet, metric, periodType, specialty, unit, reportMonthLabelRaw]);

  const toggle = (name: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const expandAll = () => {
    const all = new Set<string>();
    const walk = (n: TreeNode) => {
      all.add(n.name);
      n.children?.forEach(walk);
    };
    if (tree) walk(tree);
    setExpanded(all);
  };

  const collapseAll = () => {
    const base = new Set<string>();
    if (tree) base.add(tree.name);
    setExpanded(base);
  };

  const visiblePeriods = useMemo(() => {
    if (selectedPeriods.length === 0) return periods;
    return periods.filter((p) => selectedPeriods.includes(p));
  }, [periods, selectedPeriods]);

  const availableMetrics = sheetMetrics[sheet] || [];

  const formatValue = (v: number | null | undefined): string => {
    if (v == null) return "-";
    if (Math.abs(v) < 100) return v.toFixed(1);
    return Math.round(v).toLocaleString();
  };

  const matchesSearch = (node: TreeNode): boolean => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return node.name.toLowerCase().includes(term);
  };

  const filterNode = (node: TreeNode, depth: number): TreeNode | null => {
    const passesLevel = entityLevel === "" || node.level === entityLevel || (entityLevel === "specialty" && node.level === "unit");

    let filteredChildren: TreeNode[] = [];
    if (node.children) {
      for (const child of node.children) {
        const filtered = filterNode(child, depth + 1);
        if (filtered) filteredChildren.push(filtered);
      }
    }

    const passesSearch = matchesSearch(node) || filteredChildren.length > 0;

    if ((passesLevel || filteredChildren.length > 0) && passesSearch) {
      return { ...node, children: filteredChildren };
    }
    return null;
  };

  const displayTree = useMemo(() => {
    if (!tree) return null;
    if (entityLevel === "") return tree;
    if (entityLevel === "group") return { ...tree, children: [] };
    const filtered = filterNode(tree, 0);
    return filtered;
  }, [tree, entityLevel, searchTerm]);

  const renderRow = (node: TreeNode, depth: number): React.ReactNode => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expanded.has(node.name);
    const indent = depth * 18;

    return (
      <>
        <tr
          key={node.name + depth}
          className={`border-b border-slate-100 hover:bg-slate-50 ${depth === 0 ? "bg-slate-50 font-semibold" : ""}`}
        >
          <td
            className="py-2 px-3 whitespace-nowrap"
            style={{ paddingLeft: `${12 + indent}px` }}
          >
            <div className="flex items-center gap-1.5">
              {hasChildren ? (
                <button
                  onClick={() => toggle(node.name)}
                  className="p-0.5 hover:bg-slate-200 rounded shrink-0"
                >
                  {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
              ) : (
                <span className="w-5 shrink-0" />
              )}
              <span className={depth === 0 ? "text-slate-900" : "text-slate-700"}>{node.name}</span>
              <span className="text-xs text-slate-400 capitalize ml-1">{node.level}</span>
            </div>
          </td>
          {visiblePeriods.map((p) => {
            const val = node.values[p];
            const delta = node.deltas[p];
            return (
              <td key={p} className="py-2 px-2 text-right whitespace-nowrap">
                <span className={depth === 0 ? "font-semibold text-slate-900" : "text-slate-700"}>
                  {formatValue(val)}
                </span>
                {delta != null && (
                  <span className={`ml-1.5 text-xs ${delta >= 0 ? "text-green-600" : "text-red-500"}`}>
                    {delta > 0 ? "+" : ""}{delta}%
                  </span>
                )}
              </td>
            );
          })}
        </tr>
        {isExpanded && node.children?.map((child) => renderRow(child, depth + 1))}
      </>
    );
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto h-full flex flex-col">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Data Analysis</h1>
          <p className="text-slate-500 mt-1 text-sm">
            Drilldown from GTS totals to specialty, unit, and doctor level
          </p>
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <Filter className="w-4 h-4" />
          {showFilters ? "Hide filters" : "Show filters"}
        </button>
      </div>

      {showFilters && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Sheet</label>
              <select
                value={sheet}
                onChange={(e) => setSheet(e.target.value)}
                className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm w-full"
              >
                {sheets.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-slate-600 mb-1">Metric</label>
              <select
                value={metric}
                onChange={(e) => setMetric(e.target.value)}
                className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm w-full"
              >
                {availableMetrics.map((m) => <option key={m} value={m}>{m}</option>)}
                {availableMetrics.length === 0 && <option value={metric}>{metric}</option>}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Period</label>
              <select
                value={periodType}
                onChange={(e) => { setPeriodType(e.target.value); setSelectedPeriods([]); }}
                className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm w-full"
              >
                {PERIOD_TYPE_OPTIONS[sheet]?.map((p) => {
                  const label = PERIOD_TYPES.find((pt) => pt.value === p)?.label || p;
                  return <option key={p} value={p}>{label}</option>;
                }) || PERIOD_TYPES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Specialty</label>
              <select
                value={specialty}
                onChange={(e) => setSpecialty(e.target.value)}
                className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm w-full"
              >
                <option value="">All specialties</option>
                {availableSpecialties.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Entity level</label>
              <select
                value={entityLevel}
                onChange={(e) => setEntityLevel(e.target.value)}
                className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm w-full"
              >
                {ENTITY_LEVELS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Unit filter</label>
              <select
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm w-full"
              >
                <option value="">All units</option>
                {tree?.children?.filter((c) => c.level === "unit").map((u) => (
                  <option key={u.name} value={u.name}>{u.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Search */}
          <div className="mt-3 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search entity..."
              className="w-full sm:w-80 border border-slate-300 rounded-lg pl-9 pr-3 py-1.5 text-sm focus:ring-2 focus:ring-trofa-700 focus:outline-none"
            />
          </div>

          <div className="mt-3 flex items-start gap-2 text-xs text-slate-500 bg-slate-50 rounded-lg p-2">
            <Info className="w-4 h-4 text-trofa-700 shrink-0 mt-0.5" />
            <span>
              Specialty list is filtered to the specialties that actually have data for the selected sheet/metric/period. If a specialty is missing, the uploaded daily report does not include it for this view.
            </span>
          </div>

          {/* Period selector */}
          {periods.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4 text-slate-400" />
                <span className="text-xs font-medium text-slate-600">Show periods</span>
                {reportMonthLabel && periodType === "month" && (
                  <button
                    onClick={() => setSelectedPeriods(reportMonthLabelRaw ? [reportMonthLabelRaw] : [])}
                    className="text-xs text-trofa-700 hover:underline ml-2"
                  >
                    Report month ({reportMonthLabel})
                  </button>
                )}
                <button
                  onClick={() => setSelectedPeriods(periods)}
                  className="text-xs text-trofa-700 hover:underline ml-2"
                >
                  All
                </button>
                <button
                  onClick={() => setSelectedPeriods([])}
                  className="text-xs text-trofa-700 hover:underline ml-2"
                >
                  None
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {periods.map((p) => {
                  const displayP = formatPeriod(p, reportYear, reportMonthIndex);
                  const isFutureMonth = reportMonthIndex != null && MONTHS_PT.indexOf(p) > reportMonthIndex;
                  return (
                    <button
                      key={p}
                      onClick={() => {
                        setSelectedPeriods((prev) =>
                          prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]
                        );
                      }}
                      className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                        selectedPeriods.includes(p)
                          ? "bg-trofa-700 text-white border-trofa-700"
                          : isFutureMonth
                            ? "bg-slate-100 text-slate-400 border-slate-200"
                            : "bg-white text-slate-600 border-slate-200 hover:border-trofa-400"
                      } ${p === reportMonthLabelRaw ? "font-medium" : ""}`}
                      title={isFutureMonth ? "Mês futuro (sem dados)" : ""}
                    >
                      {displayP}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-trofa-700" />
        </div>
      ) : !displayTree ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
          <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-400">No data for the selected filters.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex-1 flex flex-col">
          <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-slate-50">
            <span className="text-sm font-medium text-slate-700">
              {metric} · {periodType === "month" ? "Monthly" : periodType === "day" ? "Daily" : "Total"}
              {specialty ? ` · ${specialty}` : ""}
              {unit ? ` · ${unit}` : ""}
              {selectedPeriods.length > 0 ? ` · ${selectedPeriods.length} period(s)` : ""}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={expandAll}
                className="px-2 py-1 text-xs text-slate-600 hover:bg-slate-200 rounded transition-colors"
              >
                Expand
              </button>
              <button
                onClick={collapseAll}
                className="px-2 py-1 text-xs text-slate-600 hover:bg-slate-200 rounded transition-colors"
              >
                Collapse
              </button>
            </div>
          </div>

          <div className="overflow-auto flex-1">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10">
                <tr className="bg-slate-100 border-b border-slate-200">
                  <th className="text-left py-2.5 px-3 font-semibold text-slate-700 sticky left-0 bg-slate-100 z-20 min-w-[220px] shadow-sm">
                    Entity
                  </th>
                  {visiblePeriods.map((p) => (
                    <th
                      key={p}
                      className="text-right py-2.5 px-2 font-semibold text-slate-700 whitespace-nowrap min-w-[90px]"
                    >
                      {formatPeriod(p, reportYear, reportMonthIndex)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>{renderRow(displayTree, 0)}</tbody>
            </table>
          </div>
          <div className="px-4 py-2 bg-slate-50 text-xs text-slate-400 border-t border-slate-200">
            Showing {displayTree.children?.length || 0} top-level groups ·{" "}
            {entityLevel ? `filtered to ${ENTITY_LEVELS.find((l) => l.value === entityLevel)?.label.toLowerCase()}` : "all levels"} ·{" "}
            {visiblePeriods.length} of {periods.length} periods selected
          </div>
        </div>
      )}
    </div>
  );
}
